import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Checkbox, Form, Input, Modal, Popconfirm, Space, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { WebApiModelsMemberField, WebApiPublicSiteDtoFamilyMemberOwnedDto } from '../../Repository/eventbookyapi';
import { EmailNotificationPreference } from '../../Types/EmailNotificationPreference';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { BookyPhoneInput } from '../../Components/Forms/BookyPhoneInput';
import { BookyDynamicInput } from '../../Components/Forms/BookyDynamicInput';

interface PublicSiteFamilyMembersProps {
    /** Company-level cap on how many family members this primary member may self-add. */
    limit: number;
}

/**
 * Lets a primary member view, add, edit and delete their own household's family members from the
 * public site. Only rendered when the company's FamilyMemberSelfServiceLimit > 0. No groups - that
 * stays an admin-only concept; field set mirrors PublicSiteEditFamilyMember's own self-edit page.
 */
const PublicSiteFamilyMembers = (props: PublicSiteFamilyMembersProps) => {
    const GetText = TextService.GetText;
    const params = useParams();
    const [form] = Form.useForm();

    // Personal number is shown only when the company's PersonalNumber field is VisiblePublic.
    const [memberFields, setMemberFields] = useState<WebApiModelsMemberField[]>([]);
    const [members, setMembers] = useState<WebApiPublicSiteDtoFamilyMemberOwnedDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<WebApiPublicSiteDtoFamilyMemberOwnedDto | undefined>(undefined);
    const [busyId, setBusyId] = useState<number | null>(null);

    const loadMembers = async () => {
        try {
            const result = await PublicSiteService.GetMyFamilyMembers();
            setMembers(result);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMembers();
        if (params.companyurl) {
            PublicSiteService.GetMemberFieldsByUrl(params.companyurl).then(setMemberFields).catch(() => setMemberFields([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const atLimit = members.length >= props.limit;

    const openAdd = () => { setEditing(undefined); setFormOpen(true); };
    const openEdit = (m: WebApiPublicSiteDtoFamilyMemberOwnedDto) => { setEditing(m); setFormOpen(true); };

    useEffect(() => {
        if (!formOpen) return;
        form.setFieldsValue({
            firstName: editing?.firstName ?? '',
            lastName: editing?.lastName ?? '',
            email: editing?.email ?? '',
            phoneNumber: editing?.phoneNumber ?? '',
            personalNumber: editing?.personalNumber ?? '',
            noEmail: editing?.emailNotificationPreference === EmailNotificationPreference.NoInformationEmails,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formOpen, editing, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email || '',
                phoneNumber: values.phoneNumber || '',
                personalNumber: values.personalNumber || '',
                emailNotificationPreference: values.noEmail
                    ? EmailNotificationPreference.NoInformationEmails
                    : EmailNotificationPreference.Unspecified,
            };

            if (editing) {
                await PublicSiteService.UpdateMyFamilyMember({ memberId: editing.id!, ...payload });
            } else {
                await PublicSiteService.AddMyFamilyMember(payload);
            }

            message.success(GetText('Common_SettingsUpdated'));
            setFormOpen(false);
            await loadMembers();
        } catch (e: any) {
            if (e?.errorFields) return; // validation error - Form shows it
            message.error(GetText('EditMember_ErrorSaving'));
        }
    };

    const handleDelete = async (m: WebApiPublicSiteDtoFamilyMemberOwnedDto) => {
        setBusyId(m.id!);
        try {
            await PublicSiteService.DeleteMyFamilyMember(m.id!);
            await loadMembers();
        } catch {
            message.error(GetText('EditMember_ErrorSaving'));
        }
        setBusyId(null);
    };

    const columns: ColumnsType<WebApiPublicSiteDtoFamilyMemberOwnedDto> = [
        {
            title: GetText('MemberList_FirstNameColumn'),
            key: 'name',
            render: (_, m) => <span style={{ fontWeight: 500 }}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || '–'}</span>,
        },
        {
            title: GetText('MemberList_EmailColumn'),
            key: 'email',
            render: (_, m) => m.email,
        },
        {
            title: GetText('MemberList_PhoneColumn'),
            key: 'phone',
            render: (_, m) => m.phoneNumber,
        },
        {
            title: GetText('MemberList_ActionColumn'),
            key: 'action',
            width: 90,
            render: (_, m) => (
                <Space size="middle">
                    <EditOutlined
                        data-testid="public-family-member-edit"
                        style={{ fontSize: '18px', cursor: 'pointer' }}
                        onClick={() => openEdit(m)}
                    />
                    <Popconfirm
                        title={GetText('MemberList_DeleteMemberConfirmMessage')}
                        okText={GetText('Common_Yes')}
                        cancelText={GetText('Common_No')}
                        onConfirm={() => handleDelete(m)}
                    >
                        <DeleteOutlined
                            data-testid="public-family-member-delete"
                            style={{ fontSize: '18px', color: busyId === m.id ? '#bfbfbf' : 'red', cursor: 'pointer' }}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div data-testid="public-family-members">
            <h2>{GetText('PublicSiteFamilyMembers_Title')}</h2>
            <p style={{ color: '#8c8c8c' }}>
                {GetText('PublicSiteFamilyMembers_Counter').replace('{0}', members.length.toString()).replace('{1}', props.limit.toString())}
            </p>

            <Table
                size="small"
                rowKey={(m) => m.id!}
                dataSource={members}
                loading={loading}
                pagination={false}
                locale={{ emptyText: GetText('MemberList_EmptyMessage') }}
                columns={columns}
            />

            <Tooltip title={atLimit ? GetText('PublicSiteFamilyMembers_LimitReached') : ''}>
                <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ padding: '4px 8px' }}
                    onClick={openAdd}
                    disabled={atLimit}
                    data-testid="public-family-member-add"
                >
                    {GetText('FamilyMemberForm_AddTitle')}
                </Button>
            </Tooltip>

            <Modal
                title={editing ? GetText('FamilyMemberForm_EditTitle') : GetText('FamilyMemberForm_AddTitle')}
                open={formOpen}
                onOk={handleSave}
                onCancel={() => setFormOpen(false)}
                okText={GetText('Common_Save')}
                cancelText={GetText('Common_Cancel')}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="firstName" label={GetText('MemberList_FirstNameColumn')} rules={[{ required: true }]}>
                        <Input data-testid="public-family-firstName" />
                    </Form.Item>
                    <Form.Item name="lastName" label={GetText('MemberList_LastNameColumn')}>
                        <Input data-testid="public-family-lastName" />
                    </Form.Item>
                    <Form.Item name="email" label={GetText('MemberList_EmailColumn')} rules={[{ type: 'email' }]}>
                        <Input data-testid="public-family-email" />
                    </Form.Item>
                    <BookyPhoneInput
                        label={GetText('MemberList_PhoneColumn')}
                        name="phoneNumber"
                        onchange={(value: any) => form.setFieldValue('phoneNumber', value)}
                        placeholder={''}
                    />
                    <BookyDynamicInput fieldType={MemberFieldTypes.PersonalNumber} fieldId={null} memberFieldSettings={memberFields} />
                    <Form.Item name="noEmail" valuePropName="checked">
                        <Checkbox data-testid="public-family-noEmail">{GetText('MemberList_FamilyEmailOptOut')}</Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PublicSiteFamilyMembers;
